import {useCallback, useEffect, useMemo, useState} from 'react';

import {BackendApi} from "../../../../../api/api.ts";
import {User} from "../../../../../api/accessControl/User.ts";
import type {BackendAPITypes} from "../../../../../generated/blueprint.ts";
import {useNotifications} from "../../../../../lib/NotificationContext.tsx";

type AccessListResponse = BackendAPITypes.BACKENDPreHandleSyncApiV1PartnerGetBusinessAccessListGetResponseBody;
type AccessMember = AccessListResponse['members'][number];
type PendingInvitation = AccessListResponse['pending_invitations'][number];
type LinkableEntrepreneur =
    BackendAPITypes.BACKENDPreHandleSyncApiV1PartnerLinkableEntrepreneursGetResponseBody['entrepreneurs'][number];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function useBusinessAccess(currentUser: User, businessId: string, onAccessChanged?: () => void) {
    const api = useMemo(() => new BackendApi(currentUser.getToken()), [currentUser]);
    const {dispatch} = useNotifications();

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [members, setMembers] = useState<AccessMember[]>([]);
    const [pendingInvitations, setPendingInvitations] = useState<PendingInvitation[]>([]);
    const [entrepreneurs, setEntrepreneurs] = useState<LinkableEntrepreneur[]>([]);

    const reload = useCallback(async () => {
        setIsLoading(true);
        try {
            const [accessList, acrossBusinesses] = await Promise.all([
                api.Partner__getBusinessAccessList(businessId),
                api.Partner__linkableEntrepreneurs(businessId),
            ]);
            setMembers(accessList.members);
            setPendingInvitations(accessList.pending_invitations);
            setEntrepreneurs(acrossBusinesses.entrepreneurs);
        } catch (error) {
            console.error('Error loading business access data:', error);
            dispatch({title: 'Fout', description: 'Kon de toegangsgegevens niet laden.', type: 'error'});
        } finally {
            setIsLoading(false);
        }
    }, [api, businessId, dispatch]);

    useEffect(() => {
        reload();
    }, [reload]);

    const refreshAfterChange = useCallback(async () => {
        await reload();
        onAccessChanged?.();
    }, [reload, onAccessChanged]);

    const inviteEntrepreneur = useCallback(async (rawEmail: string): Promise<boolean> => {
        const email = rawEmail.trim();
        if (!email) {
            dispatch({title: 'E-mailadres ontbreekt', description: 'Vul een e-mailadres in.', type: 'error'});
            return false;
        }
        if (!EMAIL_PATTERN.test(email)) {
            dispatch({title: 'Ongeldig e-mailadres', description: 'Vul een geldig e-mailadres in.', type: 'error'});
            return false;
        }
        try {
            await api.Partner__inviteEntrepreneur({business_id: businessId, email});
            dispatch({title: 'Uitnodiging verstuurd', description: `${email} is uitgenodigd.`, type: 'success'});
            await refreshAfterChange();
            return true;
        } catch (error) {
            console.error('Error inviting entrepreneur:', error);
            dispatch({title: 'Fout', description: 'Het uitnodigen is mislukt. Probeer het opnieuw.', type: 'error'});
            return false;
        }
    }, [api, businessId, dispatch, refreshAfterChange]);

    const linkEntrepreneur = useCallback(async (applicationUserId: string): Promise<boolean> => {
        try {
            await api.Partner__grantEntrepreneurAccess({business_id: businessId, application_user_id: applicationUserId});
            dispatch({title: 'Ondernemer gekoppeld', description: 'De ondernemer heeft nu toegang.', type: 'success'});
            await refreshAfterChange();
            return true;
        } catch (error) {
            console.error('Error linking entrepreneur:', error);
            dispatch({title: 'Fout', description: 'Het koppelen is mislukt. Probeer het opnieuw.', type: 'error'});
            return false;
        }
    }, [api, businessId, dispatch, refreshAfterChange]);

    const resendInvitation = useCallback(async (email: string): Promise<boolean> => {
        try {
            await api.Partner__inviteEntrepreneur({business_id: businessId, email});
            dispatch({title: 'Uitnodiging opnieuw verstuurd', description: `Naar ${email}.`, type: 'success'});
            await refreshAfterChange();
            return true;
        } catch (error) {
            console.error('Error resending invitation:', error);
            dispatch({title: 'Fout', description: 'Opnieuw versturen is mislukt.', type: 'error'});
            return false;
        }
    }, [api, businessId, dispatch, refreshAfterChange]);

    return {isLoading, members, pendingInvitations, entrepreneurs, inviteEntrepreneur, linkEntrepreneur, resendInvitation};
}
